import React, {useEffect, useState} from 'react';
import {Button, Input, message, Modal, Select, Space, Table, Tooltip} from 'antd';
import axios from 'axios';
import {getConfig} from "../../../../config";
import {useProjectContext} from "../../../../context/ProjectContext";
import {
    ArrowRightOutlined,
    CopyOutlined,
    DownOutlined,
    FileExcelOutlined,
    LoginOutlined,
    LogoutOutlined,
    MergeCellsOutlined,
    SaveOutlined,
    SplitCellsOutlined,
    UndoOutlined,
    UpOutlined
} from "@ant-design/icons";
import {DeviData} from "../../../../types/DeviData";
import {JobCode} from "../../../../types/JobCodes";
import './ConstructionSectionParser.css';
import {ViewType} from "../../../../types/ViewType";

const { Option } = Select;
const { API_BASE_URL } = getConfig();

const ConstructionSectionParser: React.FC<{
    deviData: DeviData[];
    setDeviData: React.Dispatch<React.SetStateAction<DeviData[]>>;
    filteredData: DeviData[];
    setFilteredData: React.Dispatch<React.SetStateAction<DeviData[]>>;
    jobCodes: JobCode[];
    setJobCodes: React.Dispatch<React.SetStateAction<JobCode[]>>;
    selectedProject?: string;
    viewType: ViewType;
}> = ({ deviData, setDeviData, filteredData, setFilteredData, jobCodes, setJobCodes, selectedProject, viewType }) => {
    const { selectedProject: contextProject } = useProjectContext();
    const [mergeModalVisible, setMergeModalVisible] = useState(false);
    const AUTH_TOKEN = "VdjCKidks7qTEyuU6QeJtezZ3PeCHhZQ2yHJ3kPtcfTN8BjVqU";
    const [splitModalVisible, setSplitModalVisible] = useState(false);
    const [fromJobCode, setFromJobCode] = useState<string | null>(null);
    const [joinedFromJobCode, setJoinedFromJobCode] = useState<string | null>(null);
    const [toJobCode, setToJobCode] = useState<string | null>(null);
    const [newJobCodeName, setNewJobCodeName] = useState<string>("");
    const [showFullText, setShowFullText] = useState(false);
    const [originalDeviData, setOriginalDeviData] = useState<DeviData[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [filters, setFilters] = useState({
        desc: '',
        title: '',
        jobCode: '',
        price: '',
        supplement: '',
        amountDevis: '',
        amountType: '',
    });

    const handleJobCodeChange = (value: string, recordKey: string) => {
        setDeviData(prevData => {
            return prevData.map(item =>
                item.id === recordKey ? { ...item, jobCode: value } : item
            );
        });

        setFilteredData(prevData => {
            return prevData.map(item =>
                item.id === recordKey ? { ...item, jobCode: value } : item
            );
        });
    };

    const formatSwissNumber = (num: number): string => {
        return num.toLocaleString('de-CH', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        });
    };

    const toNumber = (val: unknown): number => {
        if (typeof val === 'number') return val;
        if (val == null) return 0;
        const s = String(val)
            .trim()
            .replace(/\s/g, '')                 // Leerzeichen raus
            .replace(/CHF/gi, '')               // Währung raus
            .replace(/[’‘‚']/g, '')             // alle Apostroph-Varianten entfernen
            .replace(/,/g, '.');                // Dezimal-Komma -> Punkt
        // nur Ziffern, Minus und Punkt behalten
        const cleaned = s.replace(/[^0-9.-]/g, '');
        const n = parseFloat(cleaned);
        return Number.isFinite(n) ? n : 0;
    };

    const totalAmount = filteredData.reduce((sum, item) => {
        const price = toNumber(item.price);        // z.B. "10'885" -> 10885
        const amount = toNumber(item.amountDevis); // z.B. "2.0000" -> 2
        return sum + price * amount;
    }, 0);


    const saveChangedDeviData = async () => {
        setIsSaving(true);
        try {
            const reducedDeviData = deviData.map(({ jobCode, chapter }) => ({ jobCode, chapter }));
            const payload = {
                projectNo: selectedProject,
                deviData: reducedDeviData
            };
            await axios.post(
                `${API_BASE_URL}/saveChangedNetworkPlanDevis?auth=VdjCKidks7qTEyuU6QeJtezZ3PeCHhZQ2yHJ3kPtcfTN8BjVqU&projectNo=${selectedProject}`,
                payload
            );
            message.success("Devi-Daten erfolgreich gespeichert.");
            setOriginalDeviData([...deviData]);
        } catch (error) {
            console.error("Fehler beim Speichern der Devi-Daten:", error);
            message.error("Fehler beim Speichern der Devi-Daten.");
        } finally {
            setIsSaving(false);
        }
    };

    useEffect(() => {
        if (selectedProject)    {
            handleRefetchDeviData();
        }
    }, [selectedProject]);

    const handleMergeJobCodes = () => {
        if (!fromJobCode || !toJobCode || fromJobCode === toJobCode) {
            message.error('Bitte unterschiedliche Job-Codes auswählen.');
            return;
        }
        const updatedData = deviData.map((item: DeviData) =>
            item.jobCode === fromJobCode ? { ...item, jobCode: toJobCode } : item
        );
        setDeviData(updatedData);
        setFilteredData([...updatedData]);
        message.success(`Job-Code ${fromJobCode} wurde in ${toJobCode} geändert.`);
        setMergeModalVisible(false);
    };

    const handleSplitJobCode = () => {
        if (!newJobCodeName) {
            message.error('Bitte gib einen neuen Produktnamen ein.');
            return;
        }
        const selectedJob = jobCodes.find(job => job.alias === joinedFromJobCode);
        if (!selectedJob) {
            message.error('Kein gültiger Job-Code ausgewählt.');
            return;
        }

        // Freie P-Nummer suchen
        const existingPNumbers = jobCodes
            .map(j => j.alias)
            .filter(alias => /^P\d+$/.test(alias))
            .map(alias => parseInt(alias.substring(1)))
            .filter(n => !isNaN(n));

        const nextNumber = existingPNumbers.length > 0 ? Math.max(...existingPNumbers) + 1 : 1;
        const generatedAlias = `P${nextNumber}`;
        const half = (value: number) => Math.round((value || 0) / 2);

        const updatedJob = {
            ...selectedJob,
            productOwnerHours: half(selectedJob.productOwnerHours),
            projectManagerHours: half(selectedJob.projectManagerHours),
            drawingHours: half(selectedJob.drawingHours),
            assemblyDays: half(selectedJob.assemblyDays),
        };

        const newJobCode: JobCode = {
            ...selectedJob,
            alias: generatedAlias,
            descriptionSpez: newJobCodeName,
            description: newJobCodeName,
            productOwnerHours: half(selectedJob.productOwnerHours),
            projectManagerHours: half(selectedJob.projectManagerHours),
            drawingHours: half(selectedJob.drawingHours),
            assemblyDays: half(selectedJob.assemblyDays),
        };
        const newJobCodes = jobCodes.map(j => j.alias === selectedJob.alias ? updatedJob : j).concat(newJobCode);
        setJobCodes(newJobCodes);

        message.success(`Produkt erfolgreich aufgeteilt in ${selectedJob.alias} und ${generatedAlias}`);
        setSplitModalVisible(false);
    };

    const handleFilterChange = (value: string | undefined, field: keyof typeof filters) => {
        const updatedFilters = { ...filters, [field]: value || '' };
        setFilters(updatedFilters);
        setFilteredData(
            deviData.filter(item => {
                const desc = (item.desc || '').toLowerCase();
                const title = (item.title || '').toLowerCase();
                return (
                    (!updatedFilters.desc || desc.includes(updatedFilters.desc.toLowerCase())) &&
                    (!updatedFilters.title || title.includes(updatedFilters.title.toLowerCase())) &&
                    (!updatedFilters.supplement || item.supplement === updatedFilters.supplement) &&
                    (!updatedFilters.jobCode || item.jobCode === updatedFilters.jobCode) &&
                    (!updatedFilters.price || item.price === updatedFilters.price) &&
                    (!updatedFilters.amountDevis || item.amountDevis === updatedFilters.amountDevis) &&
                    (!updatedFilters.amountType || item.amountType === updatedFilters.amountType)
                );
            })
        );
    };


    const columns = [
        {
            title: (
                <div>
                    Devi-Position
                    <Input
                        allowClear
                        onClick={(e) => e.stopPropagation()}
                        placeholder="Filter"
                        onChange={(e) => handleFilterChange(e.target.value, 'desc')}
                        style={{ width: '100%' }}
                    />
                </div>
            ),
            dataIndex: 'desc',
            width: 200,
            className: 'align-top',
            sorter: (a: { desc: any }, b: { desc: any }) => (a.desc || '').localeCompare(b.desc || ''),
            key: 'desc',
        },
        {
            title: (
                <div>
                    NT
                    <Input
                        allowClear
                        onClick={(e) => e.stopPropagation()}
                        placeholder="Filter"
                        onChange={(e) => handleFilterChange(e.target.value, "supplement")}
                        style={{ width: "100%" }}
                    />
                </div>
            ),
            dataIndex: "supplement",
            key: "supplement",
            sorter: (a: { supplement: any }, b: { supplement: any }) => (a.supplement || "").localeCompare(b.supplement || ""),
            fixed: "left" as const,
            width: 80,
            className: "align-top",
            render: (text: string) => (
                <div className="devi-position-text" title={text}>
                    {text}
                </div>
            ),
        },
        {
            title: (
                <div>
                    Titel
                    <Input
                        allowClear
                        onClick={(e) => e.stopPropagation()}
                        placeholder="Filter"
                        onChange={(e) => handleFilterChange(e.target.value, "title")}
                        style={{ width: "100%" }}
                    />
                </div>
            ),
            dataIndex: "title",
            sorter: (a: { title: any }, b: { title: any }) => (a.title || "").localeCompare(b.title || ""),
            key: "title",
            width: 500,
            className: "align-top",
            render: (text: string) => {
                const lines = text.split("\n");
                const previewText = lines.slice(0, 2).join("\n"); // Show first 2 lines only
                return (
                    <Tooltip title={!showFullText ? text : ""} placement="top">
                        <div style={{
                            whiteSpace: "pre-wrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            maxHeight: showFullText ? "none" : "3em",
                            cursor: "pointer",
                        }}>
                            {showFullText ? text : `${previewText} ${lines.length > 2 ? "[...]" : ""}`}
                        </div>
                    </Tooltip>
                );
            },
        },
        {
            title: (
                <div>
                    Produkt
                    <Select
                        allowClear
                        showSearch
                        filterOption={(input, option) =>
                            (option?.children?.toString().toLowerCase() ?? "").includes(input.toLowerCase())
                        }
                        onClick={(e) => e.stopPropagation()}
                        placeholder="Filter"
                        onChange={value => handleFilterChange(value, 'jobCode')}
                        style={{ width: '100%' }}
                    >
                        {jobCodes.map(code => (
                            <Option key={code.alias} value={code.alias} title={code.alias}>
                                {code.descriptionSpez}
                            </Option>
                        ))}
                    </Select>
                </div>
            ),
            dataIndex: 'jobCode',
            key: 'jobCode',
            sorter: (a: { jobCode: any; }, b: { jobCode: any; }) => (a.jobCode || '').localeCompare(b.jobCode || ''),
            width: 400,
            className: 'align-top',
            render: (value: string | null, record: DeviData, index: number) => {
                const copyFromAbove = () => {
                    if (index === 0) return;
                    const prev = filteredData[index - 1];
                    if (prev?.jobCode) {
                        handleJobCodeChange(prev.jobCode, record.id);
                    }
                };
                return (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <Select
                            value={value}
                            onChange={val => handleJobCodeChange(val, record.id)}
                            style={{ width: '100%' }}
                        >
                            {jobCodes.map(code => (
                                <Option key={code.alias} value={code.alias}>
                                    {code.descriptionSpez} ({code.alias})
                                </Option>
                            ))}
                        </Select>
                        {index > 0 && (
                            <Button size="small" icon={<CopyOutlined />} onClick={copyFromAbove} />
                        )}
                    </div>
                );
            }
        },
        {
            title: (
                <div>
                    Preis
                    <Select
                        allowClear
                        onClick={(e) => e.stopPropagation()}
                        placeholder="Filter"
                        onChange={value => handleFilterChange(value, 'price')}
                        style={{ width: '100%' }}
                    >
                        {Array.from(new Set(deviData.map(item => item.price).filter(Boolean))).map(value => (
                            <Option key={value} value={value as string}>{value}</Option>
                        ))}
                    </Select>
                </div>
            ),
            dataIndex: 'price',
            sorter: (a: { price: any; }, b: { price: any; }) => (a.price || '').localeCompare(b.price || ''),
            key: 'price',
            align: 'right' as 'right',
            className: 'align-top',
            render: (text: string) => <div style={{ paddingRight: '32px' }}>{text}</div>,
        },
        {
            title: (
                <div>
                    Menge
                    <Select
                        allowClear
                        onClick={(e) => e.stopPropagation()}
                        placeholder="Filter"
                        onChange={value => handleFilterChange(value, 'amountDevis')}
                        style={{ width: '100%' }}
                    >
                        {Array.from(new Set(deviData.map(item => item.amountDevis).filter(Boolean))).map(value => (
                            <Option key={value} value={value as string}>{value}</Option>
                        ))}
                    </Select>
                </div>
            ),
            dataIndex: 'amountDevis',
            sorter: (a: { amountDevis: any; }, b: { amountDevis: any; }) => (a.amountDevis || '').localeCompare(b.amountDevis || ''),
            key: 'amountDevis',
            align: 'right' as 'right',
            className: 'align-top',
            render: (text: string) => <div style={{ paddingRight: '32px' }}>{text}</div>,
        },
        {
            title: (
                <div>
                    Mengeneinheit
                    <Select
                        allowClear
                        onClick={(e) => e.stopPropagation()}
                        placeholder="Filter"
                        onChange={value => handleFilterChange(value, 'amountType')}
                        style={{ width: '100%' }}
                    >
                        {Array.from(new Set(deviData.map(item => item.amountType).filter(Boolean))).map(value => (
                            <Option key={value} value={value as string}>{value}</Option>
                        ))}
                    </Select>
                </div>
            ),
            sorter: (a: { amountType: any; }, b: { amountType: any; }) => (a.amountType || '').localeCompare(b.amountType || ''),
            dataIndex: 'amountType',
            key: 'amountType',
            align: 'right' as 'right',
            className: 'align-top',
            render: (text: string) => <div style={{ paddingRight: '32px' }}>{text}</div>,
        }
    ];

    const joinedJobCodeFromInfo = jobCodes.find(job => job.alias === joinedFromJobCode);
    const selectedJobCodeFromInfo = jobCodes.find(job => job.alias === fromJobCode);
    const selectedJobCodeToInfo = jobCodes.find(job => job.alias === toJobCode);
    const mergedJobCodeInfo = selectedJobCodeFromInfo && selectedJobCodeToInfo ? {
        productOwnerHours: selectedJobCodeFromInfo.productOwnerHours + selectedJobCodeToInfo.productOwnerHours,
        projectManagerHours: selectedJobCodeFromInfo.projectManagerHours + selectedJobCodeToInfo.projectManagerHours,
        drawingHours: selectedJobCodeFromInfo.drawingHours + selectedJobCodeToInfo.drawingHours,
        assemblyDays: selectedJobCodeFromInfo.assemblyDays + selectedJobCodeToInfo.assemblyDays,
    } : null;

    function showMergeModal() {
        setJoinedFromJobCode("");
        setFromJobCode("");
        setToJobCode("");
        setMergeModalVisible(true)
    }

    function showSplitModal() {
        setJoinedFromJobCode("");
        setFromJobCode("");
        setNewJobCodeName("");
        setToJobCode("");
        setSplitModalVisible(true)
    }

    const handleRefetchDeviData = async () => {
        setIsLoading(true);
        if (!selectedProject && !contextProject) {
            message.error("Kein Projekt ausgewählt.");
            return;
        }
        const projectNo = selectedProject || contextProject!;
        try {
            const response = await axios.get<DeviData[]>(`${API_BASE_URL}/getNetworkPlanDeviPositions?auth=VdjCKidks7qTEyuU6QeJtezZ3PeCHhZQ2yHJ3kPtcfTN8BjVqU&projectNo=${projectNo}&supplementNo=0`);
            setDeviData(response.data);
            setFilteredData(response.data);
            setOriginalDeviData(response.data);
            message.success("Daten erfolgreich neu geladen.");
        } catch (error) {
            message.error("Fehler beim Neuladen der Devi-Daten.");
        } finally {
            setIsLoading(false)
        }
    };

    const isDataChanged = () => {
        const sortAndStringify = (arr: DeviData[]) =>
            JSON.stringify([...arr].sort((a, b) => a.id.localeCompare(b.id)));

        return sortAndStringify(deviData) !== sortAndStringify(originalDeviData);
    };

    async function openCalc() {
        try {
            const response = await axios.get(`${API_BASE_URL}/getDevisCalculationLink?auth=${AUTH_TOKEN}&projectNo=${selectedProject}`);
            if (response.data) {
                window.open(response.data, '_blank');
            } else {
                message.error('Kein gültiger Link erhalten.');
            }
        } catch (error) {
            message.error('Fehler beim Abrufen der Kalkulation');
            console.error(error);
        }
    }

    return (
        <div>
            <div>
                <div style={{display: "flex", justifyContent: "space-between", alignItems: 'center'}}>
                    <div style={{fontWeight: 500, fontSize: '16px'}}>
                        ∑ {formatSwissNumber(totalAmount)} CHF
                    </div>
                    <Space style={{marginBottom: 16}}>
                        <Tooltip title="Kalk öffnen">
                            <Button
                                type="default"
                                onClick={openCalc}
                                icon={<FileExcelOutlined/>}
                            />
                        </Tooltip>
                        <Tooltip title={showFullText ? "Text einklappen" : "Vollen Text anzeigen"}>
                            <Button
                                type="default"
                                icon={showFullText ? <UpOutlined/> : <DownOutlined/>}
                                onClick={() => setShowFullText(!showFullText)}
                            >
                                {showFullText ? "Einklappen" : "Aufklappen"}
                            </Button>
                        </Tooltip>
                        <Tooltip title="Produkt zusammenführen">
                            <Button
                                value="Zusammen"
                                type="default"
                                icon={<MergeCellsOutlined/>}
                                onClick={() => showMergeModal()}>
                                Zusammenführen</Button>
                        </Tooltip>
                        <Tooltip title="Produkt aufteilen">
                            <Button
                                type="default"
                                icon={<SplitCellsOutlined/>}
                                onClick={() => showSplitModal()}>
                                Aufteilen</Button>
                        </Tooltip>
                        <Button
                            icon={<UndoOutlined/>}
                            onClick={handleRefetchDeviData}
                            style={{marginRight: 8}}
                            type="primary"
                        >
                            Daten neu laden
                        </Button>
                        {viewType === ViewType.NEW_EXTERNAL && (
                            <Button
                                type="primary"
                                icon={<SaveOutlined/>}
                                disabled={!isDataChanged()}
                                onClick={saveChangedDeviData}
                            >
                                Speichern
                            </Button>
                        )}
                    </Space>
                </div>
                <Modal
                    title="Produkt aufteilen"
                    open={splitModalVisible}
                    onCancel={() => setSplitModalVisible(false)}
                    cancelText="Abbrechen"
                    onOk={handleSplitJobCode}
                    okText="Aufteilen"
                    width={1000}
                >
                    <div style={{display: 'flex', gap: '12px', width: '100%'}}>
                        {/* Ursprung */}
                        <div style={{flex: 1}}>
                            <div>Ursprung</div>
                            <Select
                                style={{width: '100%'}}
                                placeholder="Ergebnis"
                                value={joinedFromJobCode}
                                disabled
                                onChange={setJoinedFromJobCode}
                            >
                                {jobCodes.map(job => (
                                    <Option key={job.alias} value={job.alias}>{job.descriptionSpez}</Option>
                                ))}
                            </Select>
                            {joinedJobCodeFromInfo && (
                                <div style={{border: '1px solid #ddd', padding: 12, borderRadius: 4, marginTop: 8}}>
                                    <p><strong>Sys.Pl. Stunden:</strong> {joinedJobCodeFromInfo.productOwnerHours}</p>
                                    <p><strong>PL. Stunden:</strong> {joinedJobCodeFromInfo.projectManagerHours}</p>
                                    <p><strong>Konstr. Stunden:</strong> {joinedJobCodeFromInfo.drawingHours}</p>
                                    <p><strong>Manntage Mont.:</strong> {joinedJobCodeFromInfo.assemblyDays}</p>
                                </div>
                            )}
                        </div>

                        {/* Icon */}
                        <div style={{display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
                            <LogoutOutlined style={{fontSize: 24}}/>
                        </div>

                        {/* Teilprodukt 1 */}
                        <div style={{flex: 1}}>
                            <div>Teilprodukt 1 (50%)</div>
                            <Select
                                style={{width: '100%'}}
                                placeholder="Job-Code wählen"
                                value={joinedFromJobCode}
                                onChange={setJoinedFromJobCode}
                            >
                                {jobCodes.map(job => (
                                    <Option key={job.alias} value={job.alias}>{job.descriptionSpez}</Option>
                                ))}
                            </Select>
                            {joinedJobCodeFromInfo && (
                                <div style={{border: '1px solid #ddd', padding: 12, borderRadius: 4, marginTop: 8}}>
                                    <p><strong>Sys.Pl. Stunden:</strong> {joinedJobCodeFromInfo.productOwnerHours / 2}
                                    </p>
                                    <p><strong>PL. Stunden:</strong> {joinedJobCodeFromInfo.projectManagerHours / 2}</p>
                                    <p><strong>Konstr. Stunden:</strong> {joinedJobCodeFromInfo.drawingHours / 2}</p>
                                    <p><strong>Manntage Mont.:</strong> {joinedJobCodeFromInfo.assemblyDays / 2}</p>
                                </div>
                            )}
                        </div>

                        {/* Icon */}
                        <div style={{display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
                            <ArrowRightOutlined style={{fontSize: 24}}/>
                        </div>

                        {/* Teilprodukt 2 */}
                        <div style={{flex: 1}}>
                            <div>Teilprodukt 2 (50%)</div>
                            <Input
                                style={{width: '100%'}}
                                placeholder="Neuen Produktnamen eingeben"
                                value={newJobCodeName}
                                onChange={e => setNewJobCodeName(e.target.value)}
                            />
                            {joinedJobCodeFromInfo && (
                                <div style={{border: '1px solid #ddd', padding: 12, borderRadius: 4, marginTop: 8}}>
                                    <p><strong>Sys.Pl. Stunden:</strong> {joinedJobCodeFromInfo.productOwnerHours / 2}
                                    </p>
                                    <p><strong>PL. Stunden:</strong> {joinedJobCodeFromInfo.projectManagerHours / 2}</p>
                                    <p><strong>Konstr. Stunden:</strong> {joinedJobCodeFromInfo.drawingHours / 2}</p>
                                    <p><strong>Manntage Mont.:</strong> {joinedJobCodeFromInfo.assemblyDays / 2}</p>
                                </div>
                            )}
                        </div>
                    </div>
                </Modal>
                <Modal
                    title="Produkt zusammenführen"
                    open={mergeModalVisible}
                    onCancel={() => setMergeModalVisible(false)}
                    cancelText="Abbrechen"
                    onOk={handleMergeJobCodes}
                    okText="Zusammenführen"
                    width={1000}
                >
                    <div style={{ display: 'flex', gap: '12px', width: '100%' }}>
                        {/* Von */}
                        <div style={{ flex: 1 }}>
                            <div>Von:</div>
                            <Select
                                style={{ width: '100%' }}
                                placeholder="Job-Code wählen"
                                value={fromJobCode}
                                onChange={setFromJobCode}
                            >
                                {jobCodes.map(job => (
                                    <Option key={job.alias} value={job.alias}>
                                        {job.descriptionSpez}
                                    </Option>
                                ))}
                            </Select>
                            {selectedJobCodeFromInfo && (
                                <div style={{ border: '1px solid #ddd', padding: 12, borderRadius: 4, marginTop: 8 }}>
                                    <p><strong>Sys.Pl. Stunden:</strong> {selectedJobCodeFromInfo.productOwnerHours}</p>
                                    <p><strong>PL. Stunden:</strong> {selectedJobCodeFromInfo.projectManagerHours}</p>
                                    <p><strong>Konstr. Stunden:</strong> {selectedJobCodeFromInfo.drawingHours}</p>
                                    <p><strong>Manntage Mont.:</strong> {selectedJobCodeFromInfo.assemblyDays}</p>
                                </div>
                            )}
                        </div>

                        {/* Icon */}
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <ArrowRightOutlined style={{ fontSize: 24 }} />
                        </div>

                        {/* Zu */}
                        <div style={{ flex: 1 }}>
                            <div>Zu:</div>
                            <Select
                                style={{ width: '100%' }}
                                placeholder="Produkt wählen"
                                value={toJobCode}
                                onChange={setToJobCode}
                            >
                                {jobCodes.filter(job => job.alias !== fromJobCode).map(job => (
                                    <Option key={job.alias} value={job.alias}>
                                        {job.descriptionSpez}
                                    </Option>
                                ))}
                            </Select>
                            {selectedJobCodeToInfo && (
                                <div style={{ border: '1px solid #ddd', padding: 12, borderRadius: 4, marginTop: 8 }}>
                                    <p><strong>Sys.Pl. Stunden:</strong> {selectedJobCodeToInfo.productOwnerHours}</p>
                                    <p><strong>PL. Stunden:</strong> {selectedJobCodeToInfo.projectManagerHours}</p>
                                    <p><strong>Konstr. Stunden:</strong> {selectedJobCodeToInfo.drawingHours}</p>
                                    <p><strong>Manntage Mont.:</strong> {selectedJobCodeToInfo.assemblyDays}</p>
                                </div>
                            )}
                        </div>

                        {/* Icon */}
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <LoginOutlined style={{ fontSize: 24 }} />
                        </div>

                        {/* Ergebnis */}
                        <div style={{ flex: 1 }}>
                            <div>Ergebnis:</div>
                            <Select
                                style={{ width: '100%' }}
                                placeholder="Ergebnis"
                                value={toJobCode}
                                disabled
                                onChange={setToJobCode}
                            >
                                {jobCodes.filter(job => job.alias !== fromJobCode).map(job => (
                                    <Option key={job.alias} value={job.alias}>
                                        {job.descriptionSpez}
                                    </Option>
                                ))}
                            </Select>
                            {mergedJobCodeInfo && (
                                <div style={{ border: '1px solid #ddd', padding: 12, borderRadius: 4, marginTop: 8 }}>
                                    <p><strong>Sys.Pl. Stunden:</strong> {mergedJobCodeInfo.productOwnerHours}</p>
                                    <p><strong>PL. Stunden:</strong> {mergedJobCodeInfo.projectManagerHours}</p>
                                    <p><strong>Konstr. Stunden:</strong> {mergedJobCodeInfo.drawingHours}</p>
                                    <p><strong>Manntage Mont.:</strong> {mergedJobCodeInfo.assemblyDays}</p>
                                </div>
                            )}
                        </div>
                    </div>
                </Modal>
            </div>
            <Table
                columns={columns}
                dataSource={filteredData}
                rowKey="id"
                pagination={{ pageSize: 100 }}
                loading={isLoading}
                scroll={{ y: 700, x: 'max-content' }}
                sticky
            />
        </div>
    );
};

export default ConstructionSectionParser;